import { keys } from "@/lib/i18n/portal";

// Field types the generic gateway form knows how to render + serialize.
export type GatewayFieldType = "text" | "password" | "numberList" | "mode";

export type GatewayField = {
  // Config key sent to the backend (must match the gateway's DTO).
  readonly name: string;
  readonly labelKey: string;
  readonly type: GatewayFieldType;
};

export type GatewayProvider =
  | "paymob"
  | "paytabs"
  | "kashier"
  | "fawry"
  | "stripe";

export type GatewayDescriptor = {
  readonly provider: GatewayProvider;
  readonly brandKey: string;
  readonly descKey: string;
  // Branded monogram mark (we don't ship the gateways' trademarked logos):
  // an initial on the brand's accent color.
  readonly brandColor: string;
  readonly initial: string;
  readonly onDark: boolean; // true → white text, false → near-black (light marks)
  readonly fields: readonly GatewayField[];
};

// One descriptor per gateway — the generic tile + sheet render entirely from
// this, so adding a gateway is a new entry here (once its backend adapter ships).
export const PAYMENT_GATEWAYS: readonly GatewayDescriptor[] = [
  {
    provider: "paymob",
    brandKey: keys.paymentGateway.brandPaymob,
    descKey: keys.paymentGateway.descPaymob,
    brandColor: "#5B3DF5",
    initial: "P",
    onDark: true,
    fields: [
      {
        name: "secretKey",
        labelKey: keys.paymentGateway.fieldSecretKey,
        type: "password",
      },
      {
        name: "publicKey",
        labelKey: keys.paymentGateway.fieldPublicKey,
        type: "text",
      },
      {
        name: "hmacSecret",
        labelKey: keys.paymentGateway.fieldHmacSecret,
        type: "password",
      },
      {
        name: "integrationIds",
        labelKey: keys.paymentGateway.fieldIntegrationIds,
        type: "numberList",
      },
    ],
  },
  {
    provider: "paytabs",
    brandKey: keys.paymentGateway.brandPaytabs,
    descKey: keys.paymentGateway.descPaytabs,
    brandColor: "#12B76A",
    initial: "PT",
    onDark: true,
    fields: [
      {
        name: "profileId",
        labelKey: keys.paymentGateway.fieldProfileId,
        type: "text",
      },
      {
        name: "serverKey",
        labelKey: keys.paymentGateway.fieldServerKey,
        type: "password",
      },
      {
        name: "region",
        labelKey: keys.paymentGateway.fieldRegion,
        type: "text",
      },
    ],
  },
  {
    provider: "kashier",
    brandKey: keys.paymentGateway.brandKashier,
    descKey: keys.paymentGateway.descKashier,
    brandColor: "#7C3AED",
    initial: "K",
    onDark: true,
    fields: [
      {
        name: "merchantId",
        labelKey: keys.paymentGateway.fieldMerchantId,
        type: "text",
      },
      {
        name: "apiKey",
        labelKey: keys.paymentGateway.fieldSecretKey,
        type: "password",
      },
      { name: "mode", labelKey: keys.paymentGateway.fieldMode, type: "mode" },
    ],
  },
  {
    provider: "fawry",
    brandKey: keys.paymentGateway.brandFawry,
    descKey: keys.paymentGateway.descFawry,
    brandColor: "#FDB913",
    initial: "F",
    onDark: false,
    fields: [
      {
        name: "merchantCode",
        labelKey: keys.paymentGateway.fieldMerchantCode,
        type: "text",
      },
      {
        name: "secureKey",
        labelKey: keys.paymentGateway.fieldSecureKey,
        type: "password",
      },
    ],
  },
  {
    provider: "stripe",
    brandKey: keys.paymentGateway.brandStripe,
    descKey: keys.paymentGateway.descStripe,
    brandColor: "#635BFF",
    initial: "S",
    onDark: true,
    fields: [
      {
        name: "secretKey",
        labelKey: keys.paymentGateway.fieldSecretKey,
        type: "password",
      },
      {
        name: "webhookSecret",
        labelKey: keys.paymentGateway.fieldWebhookSecret,
        type: "password",
      },
    ],
  },
];

// A gateway config value is either a plain string field or a parsed number list
// (Paymob integration IDs).
export type GatewayConfigValue = string | number[];

// Serialize the raw string field values into the backend config shape.
export function buildGatewayConfig(
  descriptor: GatewayDescriptor,
  values: Record<string, string>,
): Record<string, GatewayConfigValue> {
  const config: Record<string, GatewayConfigValue> = {};
  for (const field of descriptor.fields) {
    const raw = (values[field.name] ?? "").trim();
    if (field.type === "numberList") {
      config[field.name] = raw
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n));
    } else if (raw.length > 0) {
      config[field.name] = raw;
    }
  }
  return config;
}
